/**
 * licenseStatus.js - Ableitung von Lifecycle-/Heartbeat-Status fuer ONPREM-Deployments.
 *
 * Policy ist bewusst fest verdrahtet und spiegelt die Backend-Policy
 * (backend/app/core/onprem_license_issuer.py: WARN_DAYS=60, GRACE_DAYS=30).
 * Diese Werte sind NICHT pro Deployment konfigurierbar (ADR-0018).
 */
export const WARN_DAYS = 60;
export const GRACE_DAYS = 30;

const DAY_MS = 24 * 60 * 60 * 1000;

/** Tage bis (positiv) / seit (negativ) valid_until. null wenn kein Datum. */
export function daysUntil(isoDate, now = new Date()) {
  if (!isoDate) return null;
  const d = new Date(`${isoDate}T00:00:00Z`);
  if (isNaN(d.getTime())) return null;
  return Math.floor((d.getTime() - now.getTime()) / DAY_MS);
}

/** Tage seit dem letzten Heartbeat. null wenn nie gesehen. */
export function daysSince(iso, now = new Date()) {
  if (!iso) return null;
  const d = new Date(iso);
  if (isNaN(d.getTime())) return null;
  return Math.floor((now.getTime() - d.getTime()) / DAY_MS);
}

/**
 * Lifecycle-Status eines Deployments.
 * Rueckgabe: { key, tone } mit key in
 *   revoked | expired | grace | warn | active | unknown
 * tone in danger | warning | ok | muted  (fuer Badge-Farben).
 */
export function lifecycleStatus(dep, now = new Date()) {
  if (!dep) return { key: 'unknown', tone: 'muted', days: null };
  if (dep.revoked) return { key: 'revoked', tone: 'danger', days: null };
  const days = daysUntil(dep.valid_until, now);
  if (days === null) return { key: 'unknown', tone: 'muted', days: null };
  if (days < -GRACE_DAYS) return { key: 'expired', tone: 'danger', days };
  if (days < 0) return { key: 'grace', tone: 'warning', days };
  if (days <= WARN_DAYS) return { key: 'warn', tone: 'warning', days };
  return { key: 'active', tone: 'ok', days };
}

/**
 * Heartbeat-Status eines Deployments auf Basis last_seen.
 * Rueckgabe: { key, tone } mit key in never | silent | ok.
 * Funkstille ab GRACE_DAYS ohne Heartbeat (max_silence_days = GRACE_DAYS).
 */
export function heartbeatStatus(dep, now = new Date()) {
  if (!dep) return { key: 'never', tone: 'muted', days: null };
  const since = daysSince(dep.last_seen, now);
  if (since === null) return { key: 'never', tone: 'muted', days: null };
  if (since > GRACE_DAYS) return { key: 'silent', tone: 'danger', days: since };
  return { key: 'ok', tone: 'ok', days: since };
}

const TONE_STYLE = {
  ok:      { color: '#10b981', bg: 'rgba(16,185,129,.12)', border: 'rgba(16,185,129,.35)' },
  warning: { color: '#f59e0b', bg: 'rgba(245,158,11,.12)', border: 'rgba(245,158,11,.35)' },
  danger:  { color: '#ef4444', bg: 'rgba(239,68,68,.12)',  border: 'rgba(239,68,68,.35)' },
  muted:   { color: 'var(--text-muted)', bg: 'var(--bg-surface)', border: 'var(--border)' },
};

export function toneStyle(tone) {
  return TONE_STYLE[tone] || TONE_STYLE.muted;
}
